import { useNavigate } from "react-router-dom";
import { TextField, Button, CardMedia, Input } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ChangeEvent, useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { useFormik } from "formik";
import * as Yup from "yup";
// import "react-phone-input-2/lib/style.css";
import { showError, showToast } from "../../constants/toasts";
import useAuth from "../../hooks/useAuth";
import { UploadMedia } from "../../utils/uploadMedia";
import { usePutUpdateProfileMutation } from "../../service/auth";
import { useAppDispatch } from "../../hooks/store";
import { setUser } from "../../reducers/authSlice";
import { isValidInput } from "../../utils/validations";
import { Edit } from "@mui/icons-material";

// Validation schema
const validationSchema = Yup.object({
  firstName: Yup.string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .matches(/^[aA-zZ\s]+$/, "First name can only contain letters"),
  lastName: Yup.string()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .matches(/^[aA-zZ\s]+$/, "Last name can only contain letters"),
  email: Yup.string()
    .required("Email is required")
    .email("Please enter a valid email address"),
  phone: Yup.string()
    .notRequired()
    .test(
      "is-valid-phone",
      "Please enter a valid phone number",
      (value) => {
        if (!value) return true;
        const digits = value.replace(/\D/g, "");
        return digits.length >= 10 && digits.length <= 15;
      }
    ),
  // .test("is-valid-phone", "Please enter a valid phone number", (value) => {
  //   if (!value) return false;
  //   // Basic phone validation - you can adjust based on your requirements
  //   return value.replace(/\D/g, "").length >= 10;
  // }),
});

interface FormValues {
  firstName: string;
  lastName: string;
  countryCode: string;
  email: string;
  phone: string;
  profileImage: string;
}

const EditProfile = () => {
  const navigate = useNavigate();

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(0);
  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [updateProfile] = usePutUpdateProfileMutation();
  const user = useAuth();
  const dispatch = useAppDispatch();

  // Initial form values
  const initialValues: FormValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "",
    profileImage: "",
  };

  // Formik hook
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Here you would typically make an API call to update the profile
        console.log("Form submitted:", values);
        const body = {
          firstName: values.firstName,
          lastName: values.lastName,
          // email: values.email,
          phone: values.phone || undefined,
          countryCode: values.countryCode || undefined,
          ...(formik.values.profileImage && {
            image: formik.values.profileImage,
          }),
        };
        // Simulate API call
        // await new Promise((resolve) => setTimeout(resolve, 1000));
        const response = await updateProfile(body).unwrap();
        if (response?.code === 200) {
          dispatch(
            setUser({
              user: { ...user, ...response?.data },
            })
          );
          showToast("Profile Updated Successfully.");
          navigate("/dashboard");
        }

        // Show success message

        // You can navigate away or reset the form here
        // navigate("/manage-profile");
      } catch (error: any) {
        showError(error?.data?.message);
      }
    },
  });

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    try {
      if (!file) {
        formik.setFieldValue("profileImage", "");
        setImage("");
        return;
      }

      const res = await UploadMedia(file);
      if (res?.code === 200) {
        const imageUrl = res?.data;
        formik.setFieldValue("profileImage", imageUrl);
        setImage(imageUrl);
        showToast("Image uploaded successfully.");
      } else {
        showError(res?.message);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showError("Error uploading image. Please try again.");
    }
  };

  useEffect(() => {
    if (user) {
      formik.setFieldValue("firstName", user?.firstName || "");
      formik.setFieldValue("lastName", user?.lastName || "");
      formik.setFieldValue("email", user?.email || "");
      formik.setFieldValue("profileImage", user?.image || "");
      formik.setFieldValue("phone", user?.phone || "");
      formik.setFieldValue("countryCode", user?.countryCode || "");
    }
  }, [user]);

  console.log(formik.values, "formik.values");

  return (
    <>
      <div className="main_title">
        <h1>Edit Profile</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-profile")}>
            Manage Profile
          </button>{" "}
          - Edit Profile
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <form onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_100">
                <label htmlFor="bannerimage">Upload Image</label>
                {formik.values.profileImage ? (
                  <div className="upload_image">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia
                          component="img"
                          image={formik.values.profileImage}
                          alt="photo"
                        />
                      </figure>
                      <CloseIcon
                        onClick={() => {
                          setImage("");
                          formik.setFieldValue("profileImage", "");
                          setFileName("");
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <label className="upload_image" htmlFor="icon-button-file">
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{
                        accept: "image/png,image/jpeg",
                      }}
                      onChange={handleImageUpload}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/user_placeholder.png"
                          alt="icon"
                        />
                        <span className="upload_img_icon">
                          <img src="/static/images/edit2_icon.svg" alt="icon" />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
              </div>
              <div className="control_group w_33">
                <label htmlFor="name">First Name</label>
                <TextField
                  id="firstName"
                  placeholder="Enter First Name"
                  hiddenLabel
                  fullWidth
                  value={formik.values.firstName}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (isValidInput(val) && val?.length < 40)
                      formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="name">Last Name</label>
                <TextField
                  id="lastName"
                  placeholder="Enter Last Name"
                  hiddenLabel
                  fullWidth
                  value={formik.values.lastName}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (isValidInput(val) && val?.length < 40)
                      formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                  }
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="email">Email Address</label>
                <TextField
                  id="email"
                  placeholder="Enter Email Address"
                  hiddenLabel
                  fullWidth
                  disabled
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="phone">Phone Number</label>
                <PhoneInput
                  country={"in"}
                  placeholder="Enter Phone Number"
                  enableSearch={false}
                  autoFormat={false}
                  value={`${(formik.values as any).countryCode || ""}${formik.values.phone || ""}`}
                  onChange={(value, country: any) => {
                    const dial = country?.dialCode || "";
                    const number = value?.replace(dial, "");
                    formik.setFieldValue("phone", number);
                    formik.setFieldValue("countryCode", dial ? `+${dial}` : "");
                  }}
                  onBlur={() => formik.setFieldTouched("phone", true)}
                  inputStyle={{
                    width: "100%",
                    borderColor:
                      formik.touched.phone && formik.errors.phone
                        ? "#f44336"
                        : "",
                  }}
                />
              </div>
              {formik.touched.phone && formik.errors.phone && (
                <div
                  style={{
                    color: "#f44336",
                    fontSize: "0.75rem",
                    marginTop: "3px",
                    marginLeft: "14px",
                  }}
                >
                  {formik.errors.phone}
                </div>
              )}
            </div>
            <div className="form_btn">
              <Button type="submit" className="btn btn_primary">
                Save
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditProfile;
